<script setup lang="ts">
  import type {
    Stakeholder,
    ChangeStakeholderPostalAddressRequestPayload
  } from '@gat-ship/types'
  import { testIds } from '@gat-ship/types'
  import {
    FormField,
    FormSubmit,
    InputGroupTextarea,
    Button,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderDescription,
    ModalHeaderTitle,
    ModalSection,
    useForm,
    z
  } from '@gat-ship/vue-components'
  import { ref } from 'vue'
  import { FieldGroup } from '@gat-ship/vue-components'
  import { text } from '@gat-ship/stakeholders'
  import { useStakeholderContext } from '../../composables/useStakeholder'
  import type { ShowStakeholderUpdatePostAddressParams } from '../providers/stakeholder-modals'
  import StakeholderBtnDeletePostAddress from '../buttons/StakeholderBtnDeletePostAddress.vue'

  // Edit-modal setup (open model + injected service + staged stakeholder +
  // title/description refs) is the same idiomatic boilerplate in every modal.
  // fallow-ignore-next-line code-duplication
  const isOpen = defineModel<boolean>('open', { default: false })
  const service = useStakeholderContext()

  const _stakeholder = ref<Stakeholder>()
  const modalTitle = ref('')
  const modalDescription = ref('')

  const schema = z.object({
    stakeholderId: z.string(),
    postalAddress: z.string().optional()
  })
  type FormValues = z.infer<typeof schema>

  const form = useForm({
    defaultValues: {
      stakeholderId: '',
      postalAddress: ''
    } as FormValues,
    validators: {
      onChange: schema
    },
    onSubmit: async ({ value }) => {
      // Backend wants `null` to clear the address — an omitted key
      // leaves the field unchanged instead, so empty input has to be
      // coerced explicitly.
      const payload: ChangeStakeholderPostalAddressRequestPayload = {
        postalAddress: value.postalAddress || null
      }
      await service?.commands.changePostalAddress(value.stakeholderId, payload)
      isOpen.value = false
    }
  })

  function showModal({ stakeholder }: ShowStakeholderUpdatePostAddressParams) {
    _stakeholder.value = stakeholder
    modalTitle.value = text.groups.postalAddress(
      stakeholder.stakeholderType
    ).modals.changePostalAddress.title
    modalDescription.value = text.groups
      .postalAddress(stakeholder.stakeholderType)
      .modals.changePostalAddress.description(stakeholder.name ?? '')
    form.reset({
      stakeholderId: stakeholder.id,
      postalAddress: stakeholder.postalAddress ?? ''
    })
    isOpen.value = true
  }

  defineExpose({ showModal })
</script>

<template>
  <modal v-model:open="isOpen">
    <ModalHeader>
      <ModalHeaderTitle>
        {{ modalTitle }}
      </ModalHeaderTitle>
      <ModalHeaderDescription v-if="modalDescription">
        {{ modalDescription }}
      </ModalHeaderDescription>
    </ModalHeader>
    <ModalContent>
      <ModalSection>
        <FieldGroup>
          <FormField :form="form" name="postalAddress" label="Postal Address">
            <template #default="{ field }">
              <InputGroupTextarea
                :id="field.name"
                :model-value="field.state.value ?? ''"
                :data-testid="
                  testIds.stakeholders.forms.postalAddress.addressInput
                "
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
        </FieldGroup>
      </ModalSection>
      <ModalSection v-if="_stakeholder">
        <StakeholderBtnDeletePostAddress
          class="w-fit"
          :stakeholder="_stakeholder"
          @saving="isOpen = false"
        />
      </ModalSection>
    </ModalContent>
    <ModalFooter>
      <FormSubmit :form="form" label="Save Changes" />
      <Button variant="ghost" @click="isOpen = false"> Cancel </Button>
    </ModalFooter>
  </modal>
</template>
